import type {
  ApiErrorBody,
  ApiErrorCode,
  AuditPage,
  AuditSummary,
  CatalogSnapshot,
  ChainVerificationResult,
  CompletionCatalog,
  DataSourceConfig,
  DataSourceSummary,
  DataSourceUpsertInput,
  DecideAccessInput,
  ExecuteQueryRequest,
  ExportFormat,
  GrantUnmaskInput,
  PolicyMode,
  PolicyRule,
  PolicyRuleInput,
  QueryHistoryEntry,
  QueryResult,
  RuleScope,
  SavedQuery,
  SavedQueryInput,
  SessionUser,
  SetExportPrivilegeInput,
  UnclassifiedObject,
  UpdateUserInput,
  UserAdminView,
  ValidateQueryResponse,
} from '@idas/shared';
import { REAUTH_ERROR_CODES } from '@idas/shared';

/**
 * Thin fetch wrapper. Every response is either typed data or an ApiError —
 * callers never inspect status codes.
 */
export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly traceId: string,
    readonly details?: unknown,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the fix is to sign in again rather than to change the request. */
  get needsReauth(): boolean {
    return REAUTH_ERROR_CODES.has(this.code);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { raw?: boolean } = {},
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // The session lives in an HttpOnly cookie, so it must be sent explicitly.
    // It is never readable from JavaScript, which is what makes XSS unable to
    // steal it.
    credentials: 'same-origin',
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    let body: ApiErrorBody;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = {
        code: 'INTERNAL',
        message: `The server returned ${response.status}.`,
        traceId: response.headers.get('X-Request-Id') ?? 'unknown',
      };
    }
    throw new ApiError(body.code, body.message, body.traceId, body.details, response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  auth: {
    me: () => request<SessionUser>('/api/auth/me'),
    loginUrl: () => request<{ url: string }>('/api/auth/login-url'),

    /** Public: authorised by the short-lived token in the redirect, not a session. */
    submitAccessPurpose: (token: string, purpose: string) =>
      request<{ ok: true }>('/api/auth/access-request/purpose', {
        method: 'POST',
        body: JSON.stringify({ token, purpose }),
      }),
    logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  },

  datasources: {
    list: () => request<{ datasources: DataSourceSummary[] }>('/api/datasources'),

    /**
     * Every connection, granted or not — what the admin screens need in order
     * to offer a choice. `list` is filtered to the caller's own grants.
     */
    listAll: () => request<{ datasources: DataSourceSummary[] }>('/api/datasources/all'),

    /**
     * Host, port, username and SSL for one connection — what the edit form
     * needs to show. Never the password.
     */
    config: (id: string) => request<DataSourceConfig>(`/api/datasources/${id}/config`),

    create: (body: DataSourceUpsertInput) =>
      request<DataSourceSummary>('/api/datasources', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    update: (id: string, body: DataSourceUpsertInput) =>
      request<DataSourceSummary>(`/api/datasources/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    remove: (id: string) =>
      request<{ ok: true }>(`/api/datasources/${id}`, { method: 'DELETE' }),

    test: (id: string) =>
      request<{
        reachable: boolean;
        latencyMs: number | null;
        error: string | null;
        readOnly: boolean | null;
        serverVersion: string | null;
        warning: string | null;
      }>(`/api/datasources/${id}/test`, { method: 'POST' }),
  },

  users: {
    list: (params: { status?: string; search?: string } = {}) => {
      const search = new URLSearchParams();
      if (params.status) search.set('status', params.status);
      if (params.search) search.set('search', params.search);
      return request<{ users: UserAdminView[] }>(`/api/users?${search}`);
    },
    pending: () => request<{ users: UserAdminView[] }>('/api/users/pending'),
    update: (id: string, body: UpdateUserInput) =>
      request<UserAdminView>(`/api/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    decideAccess: (id: string, body: DecideAccessInput) =>
      request<UserAdminView>(`/api/users/${id}/access-decision`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    setDataSourceAccess: (id: string, body: { dataSourceIds: string[]; reason: string }) =>
      request<UserAdminView>(`/api/users/${id}/datasource-access`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    setExportPrivilege: (id: string, body: SetExportPrivilegeInput) =>
      request<UserAdminView>(`/api/users/${id}/export-privilege`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    grantUnmask: (id: string, body: GrantUnmaskInput) =>
      request<UserAdminView>(`/api/users/${id}/unmask`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    revokeUnmask: (id: string, reason: string) =>
      request<UserAdminView>(`/api/users/${id}/unmask`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      }),
  },

  catalog: {
    snapshot: (dataSourceId: string) =>
      request<CatalogSnapshot>(`/api/catalog/${dataSourceId}`),
    completions: (dataSourceId: string) =>
      request<CompletionCatalog>(`/api/catalog/${dataSourceId}/completions`),
    refresh: (dataSourceId: string) =>
      request<{ tables: number; added: string[]; dropped: string[] }>(
        `/api/catalog/${dataSourceId}/refresh`,
        { method: 'POST' },
      ),

    unclassified: (dataSourceId?: string) =>
      request<{ objects: UnclassifiedObject[] }>(
        `/api/catalog/review/unclassified${dataSourceId ? `?dataSourceId=${dataSourceId}` : ''}`,
      ),

    acknowledge: (body: { dataSourceId: string; database: string; table: string }) =>
      request<{ ok: true }>('/api/catalog/review/acknowledge', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  query: {
    validate: (
      body: { dataSourceId: string; sql: string; database?: string },
      signal?: AbortSignal,
    ) =>
      request<ValidateQueryResponse>('/api/query/validate', {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      }),

    execute: (body: ExecuteQueryRequest, signal?: AbortSignal) =>
      request<QueryResult>('/api/query/execute', {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      }),

    /**
     * Cancels a query still in flight, by the id the browser generated.
     *
     * The server's run id only arrives with the response, so it is useless for
     * cancelling the request you are still waiting on.
     */
    cancel: (clientQueryId: string) =>
      request<{ cancelled: boolean }>('/api/query/cancel', {
        method: 'POST',
        body: JSON.stringify({ clientQueryId }),
      }),

    history: (params: { userId?: string; limit?: number } = {}) => {
      const search = new URLSearchParams();
      if (params.userId) search.set('userId', params.userId);
      if (params.limit) search.set('limit', String(params.limit));
      return request<{ entries: QueryHistoryEntry[] }>(`/api/query/history?${search}`);
    },

    /**
     * Exports bypass the JSON wrapper: the response is a file, and the browser
     * must be handed a Blob rather than a parsed body.
     */
    async export(body: { queryRunId: string; format: ExportFormat; reason: string }) {
      const response = await fetch('/api/query/export', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = (await response.json()) as ApiErrorBody;
        throw new ApiError(error.code, error.message, error.traceId, error.details, response.status);
      }

      const disposition = response.headers.get('Content-Disposition') ?? '';
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export.csv';

      return {
        blob: await response.blob(),
        filename,
        rowCount: Number(response.headers.get('X-Row-Count') ?? 0),
        truncated: response.headers.get('X-Truncated') === 'true',
      };
    },
  },

  savedQueries: {
    list: () => request<{ queries: SavedQuery[] }>('/api/saved-queries'),

    create: (body: SavedQueryInput) =>
      request<SavedQuery>('/api/saved-queries', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    update: (id: string, body: SavedQueryInput) =>
      request<SavedQuery>(`/api/saved-queries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    remove: (id: string) =>
      request<{ ok: true }>(`/api/saved-queries/${id}`, { method: 'DELETE' }),

    /** Replaces the set of people who can see this query. */
    setShares: (id: string, body: { userIds: string[] }) =>
      request<SavedQuery>(`/api/saved-queries/${id}/shares`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  policy: {
    mode: () => request<{ mode: PolicyMode }>('/api/policy/mode'),

    setMode: (body: { mode: PolicyMode; reason: string }) =>
      request<{ mode: PolicyMode }>('/api/policy/mode', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    rules: (dataSourceId?: string) =>
      request<{ rules: PolicyRule[] }>(
        `/api/policy/rules${dataSourceId ? `?dataSourceId=${dataSourceId}` : ''}`,
      ),

    createRule: (body: PolicyRuleInput) =>
      request<PolicyRule>('/api/policy/rules', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    updateRule: (id: string, body: PolicyRuleInput) =>
      request<PolicyRule>(`/api/policy/rules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    deleteRule: (id: string) =>
      request<{ ok: true }>(`/api/policy/rules/${id}`, { method: 'DELETE' }),

    /** Dry-runs a pattern against the catalog so its blast radius is visible. */
    testPattern: (body: { pattern: string; scope: RuleScope; dataSourceId: string }) =>
      request<{ matched: string[]; total: number; truncated: boolean }>(
        '/api/policy/rules/test',
        { method: 'POST', body: JSON.stringify(body) },
      ),
  },

  audit: {
    list: (params: Record<string, string | undefined>) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value) search.set(key, value);
      }
      return request<AuditPage>(`/api/audit?${search}`);
    },
    summary: (days = 7) => request<AuditSummary>(`/api/audit/summary?days=${days}`),
    verify: () => request<ChainVerificationResult>('/api/audit/verify', { method: 'POST' }),
  },
};

/** Triggers a browser download without leaving the page. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers; a tick is
  // enough for the navigation to have started.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
